// Two-way sync between location.hash and the running tour's state.
//
// Hash shape: #room=living&variant=modern&yaw=120.5&pitch=-5.2&time=17.5
//
// • Boot: parse the hash, return overrides so App.start can apply them before
//   firing the initial room event (this avoids emitting twice).
// • Runtime: subscribe to RoomManager / VariantManager / SunSystem events plus
//   a per-frame camera sample; debounce writes back to the hash so a user
//   drag-looking doesn't trash the History API. Uses replaceState so the back
//   button stays clean.
// • Cross-tab paste: a hashchange event re-applies via the public manager
//   APIs, with a short writer suspension so the apply doesn't fight itself.
//
// This is the Day 1 foundation for the AI variant track — generated variants
// need a stable URL slot to live in so they survive a refresh or a re-share.

import type { RoomManager } from '../playcanvas/roomManager';
import type { VariantManager } from '../playcanvas/variantManager';
import type { CameraControls } from '../playcanvas/cameraControls';
import type { SunSystem } from '../playcanvas/sunSystem';
import type { Room, RoomsConfig } from './types';

export interface ParsedUrlState {
  roomId: string | null;
  variantId: string | null;
  yaw: number | null;
  pitch: number | null;
  hoursOfDay: number | null;
}

const WRITE_DEBOUNCE_MS = 250;
const ANGLE_PRECISION = 1;
const TIME_PRECISION = 2;
/** Minimum camera-angle delta (deg) before a write is scheduled — prevents
 *  per-frame thrash while the user drag-looks at sub-pixel resolution. */
const ANGLE_WRITE_THRESHOLD_DEG = 0.5;
/** Minimum time-of-day delta (hours) — slider already debounces by step. */
const TIME_WRITE_THRESHOLD = 0.02;

export function parseUrlState(hash: string = window.location.hash): ParsedUrlState {
  const out: ParsedUrlState = { roomId: null, variantId: null, yaw: null, pitch: null, hoursOfDay: null };
  if (!hash || hash.length < 2) return out;
  const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(stripped);
  const room = params.get('room');
  if (room) out.roomId = room;
  const variant = params.get('variant');
  if (variant) out.variantId = variant;
  const yaw = numOrNull(params.get('yaw'));
  if (yaw !== null) out.yaw = yaw;
  const pitch = numOrNull(params.get('pitch'));
  if (pitch !== null) out.pitch = Math.max(-89, Math.min(89, pitch));
  const time = numOrNull(params.get('time'));
  if (time !== null) out.hoursOfDay = ((time % 24) + 24) % 24;
  return out;
}

function numOrNull(s: string | null): number | null {
  if (s === null || s === '') return null;
  const v = parseFloat(s);
  return Number.isFinite(v) ? v : null;
}

/**
 * Apply room/variant overrides to the config **before** managers are
 * constructed. We mutate `defaultRoomId` and the chosen room's
 * `defaultVariant` so the manager init path naturally lands on the right
 * state — no spurious 'api' events fired before `fireInitial`. Returns the
 * resolved room id so the caller can chain.
 */
export function applyInitialConfigOverrides(config: RoomsConfig, state: ParsedUrlState): {
  roomId: string;
  variantId: string | null;
} {
  let resolvedRoom: Room | undefined;
  if (state.roomId) {
    resolvedRoom = config.rooms.find((r) => r.id === state.roomId);
    if (resolvedRoom) {
      config.defaultRoomId = resolvedRoom.id;
    }
  }
  if (!resolvedRoom) {
    resolvedRoom =
      config.rooms.find((r) => r.id === config.defaultRoomId) ?? config.rooms[0];
  }
  if (!resolvedRoom) {
    throw new Error('[urlStateSync] rooms.json has no rooms');
  }
  let resolvedVariantId: string | null = null;
  if (state.variantId) {
    const found = resolvedRoom.variants.find((v) => v.id === state.variantId);
    if (found) {
      resolvedRoom.defaultVariant = found.id;
      resolvedVariantId = found.id;
    }
  }
  return { roomId: resolvedRoom.id, variantId: resolvedVariantId };
}

export interface UrlStateSyncDeps {
  config: RoomsConfig;
  rooms: RoomManager;
  variants: VariantManager;
  controls: CameraControls;
  sun: SunSystem;
}

export class UrlStateSync {
  private readonly deps: UrlStateSyncDeps;
  private readonly disposers: Array<() => void> = [];
  private writeTimer: number | null = null;
  private suspendWriteUntilMs = 0;
  private lastWrittenHash = '';
  private lastSampledYaw = Number.NaN;
  private lastSampledPitch = Number.NaN;
  private started = false;

  constructor(deps: UrlStateSyncDeps) {
    this.deps = deps;
  }

  /** Apply yaw / pitch / time-of-day overrides from a parsed URL. Call after
   *  managers are wired and the initial camera has been seated, so the
   *  override beats the room's authored initialYaw/Pitch. */
  applyInitialCameraAndSun(state: ParsedUrlState): void {
    if (state.yaw !== null && state.pitch !== null) {
      this.deps.controls.setLook(state.yaw, state.pitch, { snap: true });
    } else if (state.yaw !== null) {
      const cur = this.deps.controls.getLook();
      this.deps.controls.setLook(state.yaw, cur.pitch, { snap: true });
    } else if (state.pitch !== null) {
      const cur = this.deps.controls.getLook();
      this.deps.controls.setLook(cur.yaw, state.pitch, { snap: true });
    }
    if (state.hoursOfDay !== null) {
      this.deps.sun.setTimeOfDay(state.hoursOfDay);
    }
  }

  /** Subscribe to managers and start writing the hash. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.disposers.push(this.deps.rooms.on(() => this.scheduleWrite()));
    this.disposers.push(this.deps.variants.on(() => this.scheduleWrite()));
    this.disposers.push(this.deps.sun.on(() => this.scheduleWrite()));
    window.addEventListener('hashchange', this.onHashChange);
    // Force an initial write so the URL reflects what's on screen even if the
    // user never interacts. writeNow's equality check makes this a no-op when
    // the user already landed on a hash that matches the resolved state.
    this.scheduleWrite();
  }

  /**
   * Per-frame hook — call from the existing app.on('update') tap in App so we
   * don't register a second update listener. Cheap: bails out on sub-threshold
   * deltas before scheduling any work.
   */
  tickCamera(): void {
    if (!this.started) return;
    const look = this.deps.controls.getLook();
    const dyaw = Math.abs(angleDelta(look.yaw, this.lastSampledYaw));
    const dpitch = Math.abs(look.pitch - this.lastSampledPitch);
    if (
      Number.isFinite(this.lastSampledYaw) &&
      Number.isFinite(this.lastSampledPitch) &&
      dyaw < ANGLE_WRITE_THRESHOLD_DEG &&
      dpitch < ANGLE_WRITE_THRESHOLD_DEG
    ) {
      return;
    }
    this.lastSampledYaw = look.yaw;
    this.lastSampledPitch = look.pitch;
    this.scheduleWrite();
  }

  destroy(): void {
    for (const d of this.disposers) d();
    this.disposers.length = 0;
    if (this.writeTimer !== null) {
      window.clearTimeout(this.writeTimer);
      this.writeTimer = null;
    }
    window.removeEventListener('hashchange', this.onHashChange);
    this.started = false;
  }

  private readonly onHashChange = (): void => {
    // External hash change (user pasted a new link or hit back/forward).
    // Suspend our writer so the apply doesn't immediately overwrite the
    // values we just read. The 'api' triggers we emit below also fire
    // listeners — they'd schedule writes — but those will land after the
    // suspension window so the final hash equals the applied state.
    this.suspendWriteUntilMs = performance.now() + WRITE_DEBOUNCE_MS * 3;
    const state = parseUrlState();
    this.applyRuntimeFromHash(state);
  };

  private applyRuntimeFromHash(state: ParsedUrlState): void {
    // Sun first — it's the only state with no transitional side effects.
    if (state.hoursOfDay !== null) {
      const cur = this.deps.sun.getState().hoursOfDay;
      if (Math.abs(state.hoursOfDay - cur) > TIME_WRITE_THRESHOLD) {
        this.deps.sun.setTimeOfDay(state.hoursOfDay);
      }
    }
    // Room — managers de-dupe same-id setRoom calls so this is safe.
    if (state.roomId) {
      const cur = this.deps.rooms.getCurrent();
      if (cur.id !== state.roomId) {
        this.deps.rooms.setRoom(state.roomId, 'api');
      }
    }
    // Variant — must be looked up on the (possibly newly active) room.
    if (state.variantId) {
      const room = this.deps.rooms.getCurrent();
      const cur = this.deps.variants.getCurrent(room);
      if (cur.id !== state.variantId) {
        this.deps.variants.setVariant(room, state.variantId, 'api');
      }
    }
    // Camera — apply last so any room-change camera reset doesn't undo it.
    if (state.yaw !== null && state.pitch !== null) {
      this.deps.controls.setLook(state.yaw, state.pitch, { snap: true });
    }
  }

  private scheduleWrite(): void {
    if (!this.started) return;
    if (performance.now() < this.suspendWriteUntilMs) return;
    if (this.writeTimer !== null) return;
    this.writeTimer = window.setTimeout(() => {
      this.writeTimer = null;
      this.writeNow();
    }, WRITE_DEBOUNCE_MS);
  }

  private writeNow(): void {
    const hash = this.composeHash();
    if (hash === this.lastWrittenHash) return;
    this.lastWrittenHash = hash;
    try {
      const url = window.location.pathname + window.location.search + hash;
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // Some hosts (file://) reject replaceState. Fall back to direct hash
      // assignment, which will fire our own hashchange listener — guarded by
      // suspendWriteUntilMs and the equality check above.
      this.suspendWriteUntilMs = performance.now() + WRITE_DEBOUNCE_MS * 2;
      window.location.hash = hash;
    }
  }

  private composeHash(): string {
    const room = this.deps.rooms.getCurrent();
    const variant = this.deps.variants.getCurrent(room);
    const look = this.deps.controls.getLook();
    const sun = this.deps.sun.getState();
    const params = new URLSearchParams();
    params.set('room', room.id);
    params.set('variant', variant.id);
    params.set('yaw', wrapYawDeg(look.yaw).toFixed(ANGLE_PRECISION));
    params.set('pitch', look.pitch.toFixed(ANGLE_PRECISION));
    params.set('time', sun.hoursOfDay.toFixed(TIME_PRECISION));
    return '#' + params.toString();
  }
}

/** Shortest signed angular delta in degrees, in [-180, 180]. NaN-safe. */
function angleDelta(a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.POSITIVE_INFINITY;
  let d = ((a - b + 540) % 360) - 180;
  if (d <= -180) d += 360;
  return d;
}

/** Wrap yaw to [-180, 180] so the URL stays canonical regardless of how
 *  many times the user spun around. Exported so the share-link builder uses
 *  the same canonicalisation as the live hash. */
export function wrapYawDeg(yaw: number): number {
  let v = ((yaw + 180) % 360) - 180;
  if (v <= -180) v += 360;
  return v;
}
